import React, { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChange, getUserData, handleGoogleRedirect } from '../services/auth';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialAuthCheck, setInitialAuthCheck] = useState(false);

  useEffect(() => {
    const startTime = performance.now();
    console.log('[AuthContext] Начало инициализации');

    // Обрабатываем redirect результат при загрузке приложения
    const checkRedirectResult = async () => {
      const redirectStart = performance.now();
      try {
        const { user, error } = await handleGoogleRedirect();
        const redirectTime = performance.now() - redirectStart;
        console.log(`[AuthContext] Redirect check завершен за ${redirectTime.toFixed(2)}ms`, { user: !!user, error });
        if (user && !error) {
          // Пользователь успешно вошел через redirect
          // onAuthStateChange обработает это автоматически
        }
      } catch (error) {
        console.error('[AuthContext] Error handling redirect result:', error);
      }
    };

    checkRedirectResult();

    const unsubscribe = onAuthStateChange(async (user) => {
      const authCheckStart = performance.now();
      console.log('[AuthContext] Auth state changed', { hasUser: !!user });
      
      setCurrentUser(user);
      
      if (user) {
        // Сначала устанавливаем базовые данные из auth (они всегда доступны)
        const baseUserData = {
              uid: user.uid,
              email: user.email,
              displayName: user.displayName,
              photoURL: user.photoURL
        };
        setUserData(baseUserData);
        console.log('[AuthContext] Базовые данные пользователя установлены');
        
        // Разрешаем рендеринг сразу после установки базовых данных
        if (!initialAuthCheck) {
          setInitialAuthCheck(true);
          setLoading(false);
          const initialTime = performance.now() - startTime;
          console.log(`[AuthContext] Первичная проверка завершена за ${initialTime.toFixed(2)}ms - разрешаем рендеринг`);
        }
        
        // Затем пытаемся получить дополнительные данные из Firestore (асинхронно, не блокируя UI)
        const firestoreStart = performance.now();
        try {
          // Устанавливаем таймаут для Firestore запроса
          const getUserDataPromise = getUserData(user.uid);
          const timeoutPromise = new Promise((resolve) => 
            setTimeout(() => resolve({ userData: null, error: null, offline: true, timeout: true }), 2000)
          );
          
          const { userData: data, error, offline, timeout } = await Promise.race([
            getUserDataPromise,
            timeoutPromise
          ]);
          
          const firestoreTime = performance.now() - firestoreStart;
          
          if (timeout) {
            console.warn(`[AuthContext] Firestore запрос превысил таймаут (${firestoreTime.toFixed(2)}ms) - используем базовые данные`);
          } else if (data && !error) {
            // Если данные получены, объединяем их с базовыми
            setUserData({
              ...baseUserData,
              ...data
            });
            console.log(`[AuthContext] Данные Firestore загружены за ${firestoreTime.toFixed(2)}ms`);
          } else if (offline) {
            console.log(`[AuthContext] Firestore офлайн (${firestoreTime.toFixed(2)}ms) - используем базовые данные`);
          } else if (error) {
            console.warn(`[AuthContext] Ошибка Firestore (${firestoreTime.toFixed(2)}ms):`, error);
          }
        } catch (error) {
          const firestoreTime = performance.now() - firestoreStart;
          console.warn(`[AuthContext] Исключение при загрузке Firestore (${firestoreTime.toFixed(2)}ms):`, error);
        }
      } else {
        setUserData(null);
        setLoading(false);
        setInitialAuthCheck(true);
        const totalTime = performance.now() - startTime;
        console.log(`[AuthContext] Пользователь не авторизован - проверка завершена за ${totalTime.toFixed(2)}ms`);
      }
    });

    return unsubscribe;
  }, [initialAuthCheck]);

  const value = {
    currentUser,
    userData,
    loading,
    setUserData
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}; 